import { MeasureFormValues } from '@lidea/analysis/desktop/shared/ui';
import {
  useMeasures,
  useUpdateMeasure,
  useZoning,
} from '@lidea/shared/data-access/analysis';
import { usePlot } from '@lidea/shared/data-access/plots';
import { ZoningStatus } from '@lidea/shared/util/types';
import { useCallback, useMemo, useState } from 'react';

import {
  persistSelectedPoint,
  readPersistedSelectedPoint,
} from '../../measures-selection-session';
import { getAdjacentPointId, sortPointsByZone } from '../../point-navigation';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface UseMeasuresPageProps {
  plotId: number;
  analysisId: number;
}

export function useMeasuresPage(props: UseMeasuresPageProps) {
  const plot = usePlot(Number(props.plotId));
  const updateMeasure = useUpdateMeasure({
    analysisId: props.analysisId,
    plotId: props.plotId,
  });
  const [selectedPoint, setSelectedPointState] = useState<number | null>(() =>
    readPersistedSelectedPoint(props.analysisId)
  );
  const zoning = useZoning({ analysisId: props.analysisId, enabled: true });
  const measures = useMeasures({ analysisId: props.analysisId });

  const setSelectedPoint = useCallback(
    (pointId: number | null) => {
      setSelectedPointState(pointId);
      persistSelectedPoint(props.analysisId, pointId);
    },
    [props.analysisId]
  );

  const pointsReady = useMemo(() => {
    return (
      zoning.data?.points?.features
        .map((feature) => {
          return feature.properties.status === ZoningStatus.Ready;
        })
        .filter((statusBool) => statusBool === false).length === 0
    );
  }, [zoning.data]);

  const sortedPoints = useMemo(
    () => sortPointsByZone(zoning.data?.points?.features ?? []),
    [zoning.data?.points?.features]
  );

  const selectedMeasure = useMemo(() => {
    if (selectedPoint != null && measures.data) {
      return Object.values(measures.data).find(
        (measure) => Number(measure.point.id) === Number(selectedPoint)
      );
    }
  }, [measures.data, selectedPoint]);

  const handleSubmitMeasures = (data: MeasureFormValues) => {
    // only send valid value
    const validValues = Object.keys(data).reduce((acc, key) => {
      if (data[key]) {
        acc[key] = data[key];
      }
      return acc;
    }, {} as MeasureFormValues);

    if (selectedMeasure) {
      updateMeasure.mutate({
        id: selectedMeasure.id,
        data: validValues,
      });
    }
    if (updateMeasure.isError || updateMeasure.isPaused) {
      return selectedPoint;
    }
  };

  const handlePointClick = (id: number) => {
    setSelectedPoint(id);
  };

  const navigatePoint = useCallback(
    (direction: 'prev' | 'next') => {
      const nextId = getAdjacentPointId(sortedPoints, selectedPoint, direction);
      if (nextId != null) {
        setSelectedPoint(nextId);
      }
    },
    [selectedPoint, setSelectedPoint, sortedPoints]
  );

  const handleNextPoint = useCallback(
    () => navigatePoint('next'),
    [navigatePoint]
  );

  const handlePreviousPoint = useCallback(
    () => navigatePoint('prev'),
    [navigatePoint]
  );

  const selectFirstPoint = useCallback(() => {
    if (sortedPoints[0]) {
      setSelectedPoint(sortedPoints[0].id);
    }
  }, [setSelectedPoint, sortedPoints]);

  return {
    plot,
    zoning,
    measures,
    updateMeasure,
    selectedPoint,
    setSelectedPoint,
    selectedMeasure,
    handleSubmitMeasures,
    handlePointClick,
    handleNextPoint,
    handlePreviousPoint,
    selectFirstPoint,
    pointsReady,
  };
}

export default useMeasuresPage;
